import React, { useEffect } from 'react';
import { Organisation } from '@prisma/client';
import { BrandingConfig } from '../types/branding';
import { normalizeBranding } from '../lib/branding';
import { brandingToCssVars, brandingToLegacyCssVars, brandingToInlineStyle } from '../lib/theme/brandingToCssVars';

interface ThemeProviderProps {
  organisation?: Organisation;
  children: React.ReactNode;
  ssrStyle?: React.CSSProperties;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ organisation, children, ssrStyle }) => {
  useEffect(() => {
    if (!organisation?.branding) return;

    const branding = normalizeBranding(organisation.branding);
    
    // Apply CSS custom properties for theming
    const root = document.documentElement;
    
    // Apply new --brand-* variables
    const newVars = brandingToCssVars(branding);
    Object.entries(newVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    
    // Apply legacy variables for backward compatibility
    const legacyVars = brandingToLegacyCssVars(branding);
    Object.entries(legacyVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    
    // Apply custom CSS if provided
    if (branding.customCss) {
      const styleId = 'org-custom-css';
      let styleElement = document.getElementById(styleId) as HTMLStyleElement;
      
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }
      
      styleElement.textContent = branding.customCss;
    }
    
    // Update favicon if provided
    if (branding.faviconUrl) {
      updateFavicon(branding.faviconUrl);
    }
    
    // Update page title with org name
    if (organisation.name) {
      const currentTitle = document.title;
      if (!currentTitle.includes(organisation.name)) {
        document.title = `${organisation.name} | ${currentTitle}`;
      }
    }
  }, [organisation]);

  // Wrap children with SSR style if provided (prevents FOUC)
  if (ssrStyle) {
    return <div style={ssrStyle}>{children}</div>;
  }

  return <>{children}</>;
};

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0, 0, 0';
  
  return [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ].join(', ');
}

function updateFavicon(faviconUrl: string) {
  const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
  link.type = 'image/x-icon';
  link.rel = 'shortcut icon';
  link.href = faviconUrl;
  document.getElementsByTagName('head')[0].appendChild(link);
}

/**
 * Helper function to get theme style for SSR usage
 * Prevents FOUC by applying theme variables server-side
 */
export function getThemeStyleFromOrganisation(organisation?: Organisation): React.CSSProperties {
  if (!organisation?.branding) return {};
  
  const branding = normalizeBranding(organisation.branding);
  return brandingToInlineStyle(branding);
}

/**
 * Helper function to apply theme on client-side (fallback for hydration)
 */
export function applyThemeOnClient(organisation?: Organisation): void {
  if (!organisation?.branding) return;
  
  const branding = normalizeBranding(organisation.branding);
  const root = document.documentElement;
  
  // Apply new --brand-* variables
  const newVars = brandingToCssVars(branding);
  Object.entries(newVars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  
  // Apply legacy variables for backward compatibility
  const legacyVars = brandingToLegacyCssVars(branding);
  Object.entries(legacyVars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}

// CSS Variables that can be used throughout the app
export const themeVariables = {
  // Legacy variables (backward compatibility)
  primaryColor: 'var(--primary-color, #2563eb)',
  secondaryColor: 'var(--secondary-color, #1e40af)',
  primaryColorRgb: 'var(--primary-color-rgb, 37, 99, 235)',
  secondaryColorRgb: 'var(--secondary-color-rgb, 30, 64, 175)',
  
  // New brand variables
  brandPrimary: 'var(--brand-primary, #0F766E)',
  brandSecondary: 'var(--brand-secondary, #134E4A)',
  brandAccent: 'var(--brand-accent, #22C55E)',
  brandTextOnPrimary: 'var(--brand-text-on-primary, #FFFFFF)',
  brandRadius: 'var(--brand-radius, 8px)'
};

